import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link, Typography } from '@mui/material';
import apis from '@src/apis';
import RankTable from '@src/components/RankReport/RankTable';
import Tooltip from '@src/components/Tooltip';
import { formatNumber as formatNumberUtil } from '@src/utils/formatNumber';
import useFormatNumber from '@src/hooks/useFormatNumber';
import { COLOR } from '@src/styles/color';
import LoadingOverview from './LoadingOverview';

const PackageQuantityStats = ({ startTime, endTime, isReLoad }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { formatNumber } = useFormatNumber();

  const organizationId = useSelector(
    (state) => state.user.organization.selected.id,
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isDecrease, setIsDecrease] = useState(true);
  const [total, setTotal] = useState(0);
  const [percent, setPercent] = useState(0);

  const LIMIT = 10;
  const TYPE = t('bought');
  const OBJECTS = t('pkg');
  const status = isDecrease ? t('most') : t('least');
  const statusTooltip = !isDecrease ? t('most') : t('least');

  const heads = [
    { label: t('packageCode'), valueName: 'packageCode', align: 'left' },
    { label: t('appName'), valueName: 'appInfo', align: 'left' },
    { label: t('quantity'), valueName: 'quantity', align: 'right' },
    { label: t('changed'), valueName: 'changed', align: 'right' },
  ];

  const colorChanged = (changed) => {
    if (changed > 0) return COLOR.success.base;
    if (changed < 0) return COLOR.error.base;
    return COLOR.blueGray[64];
  };

  const formatData = data.map((item) => ({
    ...item,
    appInfo: (
      <Link
        href={`/organization/${organizationId}/report?appId=${item?.app?.id}&startTime=${startTime}&endTime=${endTime}`}
      >
        {item?.app?.name}
      </Link>
    ),
    quantity: (
      <Tooltip
        title={formatNumberUtil(item?.currentBoughtTime)}
        placement="right"
        arrow
      >
        <Typography>{formatNumber(item?.currentBoughtTime)}</Typography>
      </Tooltip>
    ),
    changed: (
      <Typography variant="body2" color={() => colorChanged(item?.changed)}>{`${
        item?.changed > 0 ? '+' : ''
      }${formatNumber(item?.changed)}%`}</Typography>
    ),
  }));

  const handleChangeSort = () => {
    setIsDecrease(!isDecrease);
  };

  const fetchAppRevenueStatsData = async () => {
    try {
      setLoading(true);
      const res = await apis.stat.packageQuantityStats(organizationId, {
        startTime,
        endTime,
        limit: LIMIT,
        sortBoughtTime: isDecrease ? 'desc' : 'asc',
      });
      if (res?.status) {
        const {
          limitBoughtTime,
          data: quantityData,
          totalBoughtTime,
        } = res?.result;
        const fixedLimitBoughtTime = limitBoughtTime
          ? parseFloat(((limitBoughtTime / totalBoughtTime) * 100).toFixed(2))
          : 0;
        setTotal(limitBoughtTime || 0);
        setPercent(fixedLimitBoughtTime);
        setData(quantityData || []);
      }
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    } finally {
      setLoading(false);
      if (isFirstLoading) setIsFirstLoading(false);
    }
  };

  useEffect(() => {
    fetchAppRevenueStatsData();
  }, [startTime, endTime, isDecrease, isReLoad]);

  if (loading && isFirstLoading) return <LoadingOverview />;

  return (
    <RankTable
      data={formatData}
      heads={heads}
      title={t('topRank', {
        limit: LIMIT,
        type: TYPE,
        object: OBJECTS,
        status,
      })}
      limit={LIMIT}
      type={TYPE}
      object={OBJECTS}
      status={statusTooltip}
      onChangeSort={handleChangeSort}
      isDecrease={isDecrease}
      loading={loading}
      total={total}
      percent={percent}
    />
  );
};
export default PackageQuantityStats;
